import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Star, ShoppingBag, Shield, Truck, Heart } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { useLocale } from "../contexts/LocaleContext";
import { useWishlist } from "../contexts/WishlistContext";

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);
  const { add } = useCart();
  const { fmt } = useLocale();
  const { has, toggle } = useWishlist();
  const nav = useNavigate();

  useEffect(() => { api.get(`/products/${id}`).then(r => { setP(r.data); setActive(0); }); }, [id]);
  if (!p) return <div className="py-40 text-center text-gray-400">Loading…</div>;

  return (
    <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-12">
      <div className="grid md:grid-cols-2 gap-12">
        <div>
          <div className="aspect-square bg-[#FAF7F2] overflow-hidden mb-3">
            <img src={p.images?.[active]} alt={p.name} className="w-full h-full object-cover"/>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {p.images?.map((im, i) => (
              <button key={i} onClick={()=>setActive(i)} data-testid={`thumb-${i}`} className={`aspect-square overflow-hidden border ${active===i? "border-copper":"border-black/10"}`}>
                <img src={im} alt="" className="w-full h-full object-cover"/>
              </button>
            ))}
          </div>
        </div>
        <div className="md:pl-6">
          <div className="overline mb-3">{p.brand}</div>
          <h1 className="font-display text-4xl md:text-5xl text-dark tracking-tight">{p.name}</h1>
          <div className="flex items-center gap-4 mt-4">
            {p.rating_count > 0 && (
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <Star size={14} className="fill-copper text-copper"/> {p.rating_avg} <span className="text-gray-400">({p.rating_count})</span>
              </div>
            )}
            <span className="text-xs text-gray-500">SKU: {p.sku}</span>
          </div>
          <div className="mt-6 flex items-baseline gap-3">
            <div className="font-display text-3xl text-dark">{fmt(p.price)}</div>
            {p.original_price && p.original_price > p.price && <div className="text-gray-400 line-through text-lg">{fmt(p.original_price)}</div>}
          </div>
          <p className="text-gray-700 mt-6 leading-relaxed">{p.description}</p>

          <div className="grid grid-cols-2 gap-6 mt-10 pt-8 border-t border-black/5 text-sm">
            <Spec label="Movement" v={p.movement}/>
            <Spec label="Case Size" v={p.case_size}/>
            <Spec label="Case Material" v={p.case_material}/>
            <Spec label="Strap" v={p.strap}/>
            <Spec label="Water Resistance" v={p.water_resistance}/>
            <Spec label="Gender" v={p.gender}/>
          </div>

          <div className="mt-10 flex gap-3 items-stretch">
            <button data-testid="add-to-cart" onClick={()=>{ add(p); toast.success("Added to cart"); }} className="btn-primary flex-1 justify-center"><ShoppingBag size={14}/> Add to Cart</button>
            <button data-testid="buy-now" onClick={()=>{ add(p); nav("/checkout"); }} className="btn-gold">Buy Now</button>
            <button onClick={()=>toggle(p)} aria-label="Wishlist" className={`px-4 border ${has(p.id)?"border-red-500 text-red-500":"border-black/15 text-gray-500 hover:border-dark"}`}><Heart size={16} fill={has(p.id)?"currentColor":"none"}/></button>
          </div>
          <div className="mt-6 text-xs text-gray-500 flex items-center gap-5 flex-wrap">
            <span className="flex items-center gap-2"><Shield size={13}/> Authenticated</span>
            <span className="flex items-center gap-2"><Truck size={13}/> Insured shipping</span>
            <span className="text-gray-700">{p.stock > 0 ? `${p.stock} in stock` : "Out of stock"}</span>
          </div>
        </div>
      </div>

      {p.reviews?.length > 0 && (
        <section className="mt-24">
          <div className="overline mb-3">Owner Reviews</div>
          <h3 className="font-display text-3xl text-dark mb-8">What collectors say</h3>
          <div className="space-y-6">
            {p.reviews.map(r => (
              <div key={r.id} className="border-l-2 border-copper pl-5 py-1">
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-dark">{r.user_name}</span>
                  <span className="flex items-center gap-1 text-copper">{Array.from({length: r.rating}).map((_,i)=><Star key={i} size={12} className="fill-copper"/>)}</span>
                </div>
                <div className="text-gray-800 mt-2 font-display text-lg">{r.title}</div>
                <p className="text-gray-600 text-sm mt-1">{r.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

const Spec = ({ label, v }) => (
  <div>
    <div className="text-[10px] uppercase tracking-luxe text-gray-400 mb-1">{label}</div>
    <div className="text-gray-900">{v || "—"}</div>
  </div>
);
